import type { WorkspaceVisibility } from '@/lib/api/generated/model';

/**
 * What the two visibilities are called, everywhere (spec 196 US4): Settings, the
 * admins' console, the log's sentences, the home page and the popovers.
 *
 * One setting had four names before this — "Organization, read-only",
 * "Organization: read-only", "visible to the organization, read-only" and
 * "Shared in the organization" — each written where it was shown, so each read as
 * a different thing.
 */
export const VISIBILITY_NAME: Record<WorkspaceVisibility, string> = {
  private: 'Private',
  organization: 'Organization (read-only)',
};

/** Who can see a workspace of each visibility, in one line. */
export const VISIBILITY_LINE: Record<WorkspaceVisibility, string> = {
  private: 'Only its members can see it.',
  organization:
    'Everyone in the organization can see its work; only members change it.',
};

/**
 * The words for a person's standing in a workspace (spec 188), in one place so the
 * top bar, the band, the no-access page and the home page say the same thing.
 *
 * The popovers are a title and two short lines (spec 196 US1): they were 40–50-word
 * paragraphs, read once and skipped after.
 */
export const ACCESS_COPY = {
  viewOnlyChip: 'View only',
  viewOnlyLabel: (workspace: string) =>
    `${workspace}: view only. You are not a member.`,
  viewOnlyTitle: (workspace: string) => `You can view ${workspace}.`,
  viewOnlyLines: [
    'Shared with the organization: open anything, change nothing.',
    'Ask its owners to add you.',
  ],
  adminChip: 'Organization admin',
  adminLabel: (workspace: string) =>
    `${workspace}: acting as organization admin. You are not a member.`,
  adminTitle: (workspace: string) =>
    `You are in ${workspace} as organization admin.`,
  adminLines: [
    'You can do anything its owners can.',
    'What you change is recorded as an organization admin’s.',
  ],
  noAccessTitle: 'You don’t have access to this workspace',
  // Not "they will see your request": nothing has been asked yet (spec 193).
  noAccessBody: 'Only its members can open it. Ask its owners to add you.',
  /** The one verb for asking to join (spec 190; one name since spec 196 US4). */
  requestAccess: 'Request access',
  requestSentShort: 'Request sent',
  requestSent: (when: string) =>
    `Request sent ${when}. You’ll hear when you’re added.`,
  /** The moment of sending, on the no-access page (spec 196 US2). */
  requestSentNow: 'Request sent. You’ll hear when you’re added.',
  switchAccount: 'Switch account',
  requestsHeading: 'Requests to join',
  requestedWhen: (when: string) => `Asked ${when}`,
  addRequest: 'Add',
  dismissRequest: 'Dismiss',
  added: (name: string) => `${name} was added.`,
  dismissed: (name: string) => `${name}’s request was dismissed.`,
  backToWorkspaces: 'Back to all workspaces',
  /** Where a change control would be, for someone who may only read (spec 189). */
  membersOnly: 'Only this workspace’s members can change this.',
  membersOnlyComment: 'Only this workspace’s members can comment.',
  membersOnlyReply: 'Only this workspace’s members can reply.',
  /** A members' page reached by its address (spec 189, decision Q10). */
  membersPageTitle: 'Only members can see this',
  membersPageBody: (what: string) =>
    `${what} are for this workspace’s members. You can still open its boards, tasks, runs, files and workflows.`,
  /** The way on from a members' page: into what the reader can open (spec 193). */
  toOverview: 'Go to the Overview',
  /** Home page tabs (decision Q26, spec 200). */
  yoursEmpty:
    'You are not in any workspace yet. Create one, or open one shared with the organization.',
  /** Under a search: yours exist, just not these (spec 193). */
  yoursNoMatch: 'None of yours match.',
  sharedHint: 'Open them to follow along; only their members change them.',
  /** An org admin can change them, so they are not read-only to them (spec 193). */
  sharedHintAdmin:
    'Shared with the organization. You can manage them as organization admin.',
  /** The home page's tabs (spec 200): short, as the count follows each. */
  yoursTab: 'Yours',
  sharedTab: 'Organization',
  othersTab: 'All others',
  /** A tab picked by hand under a search that found nothing in it. */
  tabNoMatch: 'None of these match.',
  othersHint:
    'Private workspaces you are not in. You can open them as organization admin.',
} as const;
